import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageWrapper from "../pageWrapper";

//CSS
import "../0PageStyles.css";
import "./importItemsPage.css";

//Components
import Button from "../../Components/Other Components/button";
import Title from "../../Components/Other Components/Title";
import FileInputField from "../../Components/Other Components/fileInputField";

//Functions and hooks
import { BulkCreateItems } from "../../BackendAccess/ItemHooks";
import { useNotification } from "../../Contexts/NotificationContext";

const EXPECTED_COLUMNS = [
  "ID",
  "Name",
  "Barcode",
  "Unit",
  "Price",
  "SellingPrice",
  "MinStock",
];

export default function ImportItemsPage() {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const fileInputRef = useRef(null);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { t } = useTranslation("items");

  const handleFileChange = async (file) => {
    if (!file) return;
    setParseError(null);
    setImportResult(null);
    setFileName(file.name);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsedRows = XLSX.utils.sheet_to_json(firstSheet, {
        defval: "",
      });

      if (parsedRows.length === 0) {
        setParseError(t("The file contains no rows."));
        setRows([]);
        return;
      }

      const missingNameOrPrice = parsedRows.some(
        (row) => !row.Name || row.Price === ""
      );
      if (missingNameOrPrice) {
        setParseError(
          t("Every row must have at least a Name and a Price column.")
        );
        setRows([]);
        return;
      }

      setRows(parsedRows);
    } catch (err) {
      setParseError(t("Could not read this file. Make sure it's a valid CSV or Excel file."));
      setRows([]);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    showNotification(t("Importing items..."), "info");
    try {
      const items = rows.map((row) => ({
        ID: row.ID || undefined,
        Name: row.Name,
        Barcode: row.Barcode || undefined,
        Unit: row.Unit,
        Price: row.Price,
        SellingPrice: row.SellingPrice || undefined,
        MinStock: row.MinStock === "" ? undefined : row.MinStock,
      }));
      const [data, error] = await BulkCreateItems(items);
      if (error) {
        showNotification(error.err, "error");
        return;
      }
      setImportResult(data);
      if (data.failureCount === 0) {
        showNotification(
          t("Imported {{count}} item(s) successfully!", {
            count: data.successCount,
          }),
          "success"
        );
      } else {
        showNotification(
          t("Imported {{success}} item(s), {{failed}} failed.", {
            success: data.successCount,
            failed: data.failureCount,
          }),
          "info"
        );
      }
    } finally {
      setImporting(false);
    }
  };

  const clickOnCancel = () => {
    navigate(-1);
  };

  const resetFile = () => {
    setRows([]);
    setFileName("");
    setParseError(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Bulk importing items", { ns: "tutorial" }),
      message: t(
        "This page lets you add many items at once from a spreadsheet, instead of creating them one by one.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "import-items-instructions",
      title: t("File format", { ns: "tutorial" }),
      message: t(
        "This tells you exactly which columns your file needs. Only Name, Unit, and Price are required - everything else, including ID, is optional and will be filled in automatically if left out.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".fileInputContainer",
      title: t("Choose a file", { ns: "tutorial" }),
      message: t(
        "Click here to pick your CSV or Excel file. Once it's selected, you'll see a preview of every parsed row below before anything is actually created.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "import-items-import-btn",
      title: t("Import"),
      message: t(
        "Once you're happy with the preview, click here to create every row as an item. A bad row won't block the good ones - you'll get a result for each row showing exactly what succeeded and what failed, and why.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="importItems" tutorialSteps={tutorialSteps}>
      <div className="page-content">
        <Title className="mt-3">{t("Bulk Import Items")}</Title>

        <div className="container-bordered w-90 vertical mb-4 mt-4">
          <p className="mb-2" data-tutorial-id="import-items-instructions">
            {t(
              "Upload a CSV or Excel file with columns: ID (optional), Name, Barcode (optional), Unit, Price, SellingPrice (optional), MinStock (optional)."
            )}
          </p>
          <FileInputField
            ref={fileInputRef}
            accept=".csv,.xlsx,.xls"
            fileName={fileName}
            onChange={handleFileChange}
            label={t("Click to upload a CSV or Excel file")}
            changeLabel={t("Click to choose a different file")}
          />
          {parseError && (
            <p className="text-negative mt-2">{parseError}</p>
          )}
          {rows.length > 0 && !parseError && (
            <p className="mt-2">
              {t("{{count}} row(s) ready to import from {{fileName}}.", {
                count: rows.length,
                fileName,
              })}
            </p>
          )}
        </div>

        {rows.length > 0 && !parseError && (
          <div className="container-bordered w-90 vertical mb-4">
            <Title className="font-m mb-2">{t("Preview")}</Title>
            <div className="import-preview-table-wrapper">
              <table className="import-preview-table">
                <thead>
                  <tr>
                    {EXPECTED_COLUMNS.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 10).map((row, idx) => (
                    <tr key={idx}>
                      {EXPECTED_COLUMNS.map((col) => (
                        <td key={col}>
                          {row[col] === undefined ||
                          row[col] === null ||
                          row[col] === ""
                            ? "/"
                            : String(row[col])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > 10 && (
              <p className="mt-2">
                {t("...and {{count}} more row(s).", {
                  count: rows.length - 10,
                })}
              </p>
            )}
          </div>
        )}

        {importResult && (
          <div className="container-bordered w-90 vertical mb-4">
            <Title className="font-m mb-2">{t("Import Results")}</Title>
            <p>
              {t("{{success}} succeeded, {{failed}} failed.", {
                success: importResult.successCount,
                failed: importResult.failureCount,
              })}
            </p>
            {importResult.failureCount > 0 && (
              <div className="import-preview-table-wrapper mt-2">
                <table className="import-preview-table">
                  <thead>
                    <tr>
                      <th>{t("Row")}</th>
                      <th>{t("ID")}</th>
                      <th>{t("Reason")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.results
                      .filter((r) => !r.success)
                      .map((r) => (
                        <tr key={r.row}>
                          <td>{r.row + 1}</td>
                          <td>{r.ID}</td>
                          <td>
                            {t(r.errorKey, { ns: "backend", ...r.params })}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="btn-container">
          <Button
            btnMargin={"btn-default"}
            btnStyle="btn-positive"
            onClick={handleImport}
            disabled={rows.length === 0 || !!parseError || importing}
            icon={"create"}
            tutorialId="import-items-import-btn"
          >
            {t("Import")}
          </Button>
          <Button
            btnMargin={"btn-default"}
            btnStyle="btn-primary"
            onClick={resetFile}
          >
            {t("Clear")}
          </Button>
          <Button
            btnMargin={"btn-default"}
            btnStyle="btn-primary"
            onClick={clickOnCancel}
          >
            {t("Cancel", { ns: "common" })}
          </Button>
        </div>
      </div>
    </PageWrapper>
  );
}
